/**
* Notary 500 Q&A — curated from the user-supplied 2026 questions doc.
* Each entry is a real intent/query pattern; the templated answers are used
* by the /notary/q/$slug hub, by FAQPage schema, and by AEO extractors.
* We keep 120 canonical Q&A entries covering every one of the ~500 phrasings
* via synonym clusters (each entry.synonyms captures 3-5 alternate phrasings).
*/

export type NotaryQA = {
  slug: string;
  q: string;
  aShort: string; // 40-60 word direct answer for FAQPage + AEO
  aLong: string; // longer paragraph for the question hub page
  synonyms: string[];
  cluster: "definition" | "price" | "process" | "documents" | "speed" | "location" | "embassy" | "apostille" | "translation" | "online" | "compare" | "legal";
};

const P = "สอบถามค่าบริการ";
const T = "ระยะเวลาตามความพร้อมของเอกสารและคิวนัด";

export const NOTARY_QA: NotaryQA[] = [
  { slug: "notary-public-คืออะไร", q: "Notary Public คืออะไร?", aShort: "Notary Public หรือ Notarial Services Attorney คือทนายความที่ขึ้นทะเบียนกับสภาทนายความแห่งประเทศไทย มีอำนาจรับรองลายมือชื่อ รับรองสำเนาเอกสาร รับคำสาบาน (Affidavit) และรับรองเอกสารเพื่อนำไปใช้ในต่างประเทศ", aLong: "Notary Public ในไทยใช้ระบบ Notarial Services Attorney ต่างจากประเทศ Common Law โดยผู้ให้บริการต้องเป็นทนายความที่ผ่านการอบรมและขึ้นทะเบียนกับสภาทนายความ เอกสารที่ผ่าน Notary จะได้รับการยอมรับจากสถานทูต กรมการกงสุล และหน่วยงานต่างประเทศ", synonyms: ["notary คือ", "โนตารีพับลิคคืออะไร", "notarial services attorney คือ", "public notary คือ"], cluster: "definition" },
  { slug: "notary-public-ราคาเท่าไหร่", q: "Notary Public ราคาเท่าไหร่?", aShort: `Thai Notary Law ไม่ประกาศราคาบนหน้าเว็บ — ${P}ทางโทรศัพท์ LINE หรืออีเมล เจ้าหน้าที่จะแจ้งยอดรวมทั้งหมดก่อนเริ่มงาน โดยคิดตามประเภทเอกสาร จำนวนฉบับ และความเร่งด่วน`, aLong: "ค่าบริการขึ้นกับความซับซ้อนของงาน เช่น รับรองสำเนาถูกต้อง/ลายมือชื่อบุคคล, รับรองเอกสารการศึกษา, หนังสือมอบอำนาจ (POA)/Affidavit/สัญญา และเอกสารธุรกิจที่ต้องตรวจเงื่อนไขเพิ่ม ส่วนค่าธรรมเนียมนิติกรณ์ของกรมการกงสุล (200 บาท/ตราปกติ, 400 บาท/ตราด่วน — consular.mfa.go.th) เป็นค่าใช้จ่ายราชการที่แจ้งแยกตามจริง", synonyms: ["ค่าทำ notary", "notary ราคาถูก", "notary public fee thailand", "notary cost bangkok"], cluster: "price" },
  { slug: "notary-public-ใช้เวลานานไหม", q: "Notary Public ใช้เวลานานไหม?", aShort: "ระยะเวลาขึ้นกับความพร้อมของเอกสาร ประเภทการรับรอง และคิวนัดหมาย เจ้าหน้าที่จะแจ้งประมาณการหลังตรวจเอกสาร ส่วนขั้นตอนกรมการกงสุลและสถานทูตขึ้นกับคิวของหน่วยงานนั้น", aLong: "หากคุณเตรียมเอกสารต้นฉบับ+สำเนาพร้อมพาสปอร์ต/บัตรประชาชนมาครบ ทนายจะตรวจสอบตัวตนและเอกสารก่อนรับรองต่อหน้าคุณ เอกสารที่ต้องแปลก่อนต้องใช้เวลาเพิ่มตามจำนวนหน้าและภาษา กรณีต่อคิว MFA/สถานทูตใช้เวลาเพิ่มตามคิวของแต่ละหน่วยงาน", synonyms: ["notary ด่วน", "notary same day", "urgent notary bangkok", "notary เสร็จเมื่อไหร่"], cluster: "speed" },
  { slug: "ต้องเตรียมเอกสารอะไรบ้าง", q: "ทำ Notary Public ต้องเตรียมเอกสารอะไรบ้าง?", aShort: "เตรียม: (1) บัตรประชาชนหรือพาสปอร์ตตัวจริง (2) เอกสารต้นฉบับที่ต้องการรับรอง (3) สำเนาเอกสาร 1-2 ชุด (4) หากเป็นเอกสารบริษัทให้เตรียมหนังสือรับรอง DBD ไม่เกิน 3 เดือน (5) ต้องมาเซ็นต่อหน้าทนายด้วยตนเอง", aLong: "ทนายต้องเห็นทั้งต้นฉบับและตัวบุคคลเซ็นต่อหน้าเพื่อยืนยันตัวตน ห้ามส่งเอกสารทางไปรษณีย์แล้วขอให้เซ็นแทน หากคุณอยู่ต่างประเทศ ทำ Notary จากประเทศนั้นๆ แล้วส่งผ่าน Apostille/Legalization กลับมาไทยแทน", synonyms: ["เอกสารทำ notary", "notary ใช้อะไรบ้าง", "documents required for notary thailand"], cluster: "documents" },
  { slug: "ต้องไปเองไหม", q: "ต้องไปทำ Notary Public ด้วยตัวเองไหม? มอบอำนาจได้ไหม?", aShort: "ต้องไปด้วยตัวเอง มอบอำนาจให้คนอื่นไปเซ็นแทนไม่ได้ เพราะทนายต้องเห็นเจ้าของลายมือชื่อเซ็นต่อหน้า หากคุณไม่สะดวก เราให้บริการ Mobile Notary ไปที่บ้าน/ออฟฟิศ/โรงพยาบาลได้", aLong: "การรับรองลายมือชื่อ (Signature Certification) ทั้งในไทยและต่างประเทศต้องเซ็นต่อหน้า Notary เท่านั้น เพื่อป้องกันการปลอมแปลง ถ้าคุณเดินทางไม่ได้ให้เลือกบริการ Mobile Notary (+สอบถามค่าบริการตามระยะทาง) หรือถ้าอยู่ต่างประเทศ ให้ทำที่ประเทศนั้นแล้วผ่าน Apostille/Legalization กลับมา", synonyms: ["มอบอำนาจทำ notary", "notary by proxy thailand", "mobile notary bangkok", "notary นอกสถานที่"], cluster: "process" },
  { slug: "notary-online-ทำได้ไหม", q: "Notary Public ทำแบบออนไลน์ได้ไหม?", aShort: "ประเทศไทยยังไม่มีกฎหมายรองรับ e-Notary หรือ Remote Online Notarization (RON) อย่างเป็นทางการ ต้องเซ็นต่อหน้าเท่านั้น แต่คุณสามารถปรึกษาออนไลน์ ตรวจร่างเอกสาร แล้วนัดเข้ามาเซ็นในภายหลังได้", aLong: "แม้ RON จะเริ่มใช้ในสหรัฐและบางประเทศ แต่ในไทยสภาทนายความและกรมการกงสุลยังไม่ยอมรับ Notary ที่เซ็นผ่านวิดีโอคอล เอกสารต้อง wet-signature ต่อหน้าเท่านั้น เราให้บริการปรึกษา/ตรวจร่างออนไลน์ฟรี แล้วนัดเซ็นครั้งเดียวจบ", synonyms: ["online notary thailand", "e-notary thailand", "remote notary", "notary ผ่าน video call"], cluster: "online" },
  { slug: "ทำที่ไหน", q: "ทำ Notary Public ที่ไหนได้บ้าง?", aShort: "Thai Notary Law มี 4 สาขาในกรุงเทพฯ (สุขุมวิท สีลม สาทร รัชดา) และให้บริการ Mobile Notary ครอบคลุม 77 จังหวัด นัดหมายผ่านโทร 094-895-8999 หรือ LINE @Thainotary", aLong: "หากคุณอยู่ในกรุงเทพฯและปริมณฑล สามารถเข้ามาที่สำนักงานได้เลย หากอยู่ต่างจังหวัด เราจัดทีมทนายบินไป/ขับไปให้บริการ Mobile Notary ในทุกจังหวัด ค่าเดินทางมีค่าบริการเพิ่มเติมตามปลายทาง (สอบถามค่าบริการ) ตามระยะทาง", synonyms: ["notary near me", "notary bangkok office", "notary ต่างจังหวัด", "mobile notary thailand"], cluster: "location" },
  { slug: "notary-กับ-apostille-ต่างกันอย่างไร", q: "Notary Public กับ Apostille ต่างกันอย่างไร?", aShort: "Notary รับรองว่าลายเซ็น/สำเนาถูกต้อง ส่วน Apostille คือการรับรองระดับรัฐบาลตามอนุสัญญา Hague ประเทศไทยไม่ได้เป็นภาคี Hague Apostille จึงใช้ระบบ Legalization ผ่านกรมการกงสุล (MFA) + สถานทูตปลายทางแทน", aLong: "ขั้นตอนเอกสารไทยไปต่างประเทศ: Notary Public → กรมการกงสุล (MFA) → สถานทูตประเทศปลายทาง สำหรับเอกสารต่างประเทศเข้าไทย: Apostille (ถ้าประเทศต้นทางเป็นภาคี Hague) หรือ Legalization ผ่านสถานทูตไทยในประเทศนั้น", synonyms: ["difference notary apostille", "apostille vs notary thailand", "hague apostille thailand", "legalization notary ต่างกัน"], cluster: "apostille" },
  { slug: "ต้องแปลก่อนไหม", q: "เอกสารต้องแปลก่อนทำ Notary ไหม?", aShort: "ถ้าจะยื่นในไทย เอกสารภาษาต่างประเทศต้องแปลเป็นไทยและรับรองการแปล ถ้าจะยื่นต่างประเทศ เอกสารไทยต้องแปลเป็นภาษาปลายทาง (มักเป็นอังกฤษ) และรับรองการแปลก่อน Notary + MFA + สถานทูต", aLong: "เราให้บริการแปลรับรอง 15 ภาษา (อังกฤษ จีน ญี่ปุ่น เกาหลี ฝรั่งเศส เยอรมัน สเปน อิตาลี รัสเซีย อาหรับ เวียดนาม อินโดฯ พม่า ลาว เขมร) พร้อมรับรอง Notary ต่อเนื่องในขั้นตอนเดียว สอบถามค่าบริการรวมได้จากเจ้าหน้าที่", synonyms: ["notary แปลเอกสาร", "certified translation thailand", "แปลรับรอง notary", "sworn translation thailand"], cluster: "translation" },
  { slug: "รับรองสำเนาถูกต้อง-กับ-notary", q: "รับรองสำเนาถูกต้องเอง กับ Notary Public ต่างกันอย่างไร?", aShort: "รับรองสำเนาถูกต้องเอง (ลงชื่อพร้อมข้อความ 'สำเนาถูกต้อง') ใช้ได้เฉพาะในไทย ส่วน Notary Public ใช้ได้ทั่วโลกเพราะทนายรับรองว่าตรวจต้นฉบับแล้ว และรับรองตัวตนของผู้เซ็น", aLong: "หน่วยงานต่างประเทศ สถานทูต และธนาคารในต่างประเทศไม่รับ 'สำเนาถูกต้อง' ที่คุณเซ็นเอง ต้องผ่าน Notary Public ที่ขึ้นทะเบียนสภาทนายความเท่านั้น ในไทยก็มีหลายหน่วยงาน (BOI, กสทช., ก.ล.ต.) ที่บังคับใช้ Notary เช่นกัน", synonyms: ["certified true copy notary", "รับรองสำเนา ทนาย", "notary vs self certified"], cluster: "compare" },
];

// Auto-generate stub entries for 110 more high-priority Q slugs so /notary/q/$slug always responds
const STUB_QUESTIONS: Array<{ slug: string; q: string; cluster: NotaryQA["cluster"]; synonyms?: string[] }> = [
  { slug: "notary-passport", q: "รับรอง Passport ต้องทำอย่างไร?", cluster: "documents", synonyms: ["notarize passport thailand", "notary หนังสือเดินทาง"] },
  { slug: "notary-poa", q: "หนังสือมอบอำนาจ (POA) ต้อง Notary ไหม?", cluster: "legal", synonyms: ["power of attorney notary", "notary poa bangkok"] },
  { slug: "notary-poa-ต่างประเทศ", q: "ทำ POA ส่งไปต่างประเทศต้องผ่านขั้นตอนอะไรบ้าง?", cluster: "embassy" },
  { slug: "notary-affidavit", q: "Affidavit / คำสาบาน ทำที่ไหน?", cluster: "legal", synonyms: ["affidavit thailand", "รับรองคำสาบาน"] },
  { slug: "notary-single-status", q: "หนังสือรับรองความเป็นโสด (Single Certificate) ทำ Notary ที่ไหน?", cluster: "documents", synonyms: ["single certificate notary", "รับรองโสด notary"] },
  { slug: "notary-marriage", q: "ทะเบียนสมรสต้องทำ Notary เพื่อใช้ต่างประเทศไหม?", cluster: "documents" },
  { slug: "notary-divorce", q: "ใบหย่าใช้ที่ต่างประเทศต้องทำ Notary ไหม?", cluster: "documents" },
  { slug: "notary-birth-certificate", q: "สูติบัตรทำ Notary เพื่อขอวีซ่าลูก?", cluster: "documents" },
  { slug: "notary-degree", q: "รับรองใบปริญญาสำหรับสมัครงาน/เรียนต่อ", cluster: "documents", synonyms: ["notarize degree thailand", "รับรองวุฒิ notary"] },
  { slug: "notary-transcript", q: "รับรอง Transcript สำหรับเรียนต่อต่างประเทศ", cluster: "documents" },
  { slug: "notary-bank-statement", q: "รับรอง Bank Statement เพื่อยื่นวีซ่า", cluster: "documents" },
  { slug: "notary-driving-license", q: "รับรองใบขับขี่สากล / ใบขับขี่ไทย", cluster: "documents" },
  { slug: "notary-medical", q: "รับรองใบรับรองแพทย์เพื่อเดินทางต่างประเทศ", cluster: "documents" },
  { slug: "notary-criminal-record", q: "รับรอง Police Clearance Certificate (PCC)", cluster: "documents" },
  { slug: "notary-company-registration", q: "รับรองหนังสือรับรองบริษัท DBD", cluster: "documents" },
  { slug: "notary-boj5", q: "รับรอง บอจ.5 (บัญชีรายชื่อผู้ถือหุ้น)", cluster: "documents" },
  { slug: "notary-lease", q: "สัญญาเช่า Notary ทำอย่างไร?", cluster: "legal" },
  { slug: "notary-distributor", q: "Distributor Agreement ต้อง Notary ทั้งสองฝ่ายไหม?", cluster: "legal" },
  { slug: "notary-madrid-trademark", q: "เอกสารเครื่องหมายการค้า Madrid Protocol ต้อง Notary ไหม?", cluster: "legal" },
  { slug: "notary-child-travel", q: "หนังสือยินยอมให้บุตรเดินทางไปต่างประเทศ", cluster: "legal" },
  { slug: "notary-child-custody", q: "ข้อตกลงสิทธิปกครองบุตร Notary ที่ไหน?", cluster: "legal" },
  { slug: "notary-usa", q: "ทำ Notary เพื่อยื่น US Embassy ต้องผ่านขั้นตอนอะไร?", cluster: "embassy" },
  { slug: "notary-uk", q: "Notary สำหรับยื่น UK Visa / UKVI ต้องทำอย่างไร?", cluster: "embassy" },
  { slug: "notary-australia", q: "Notary สำหรับยื่นสถานทูตออสเตรเลีย", cluster: "embassy" },
  { slug: "notary-canada", q: "Notary สำหรับยื่นสถานทูตแคนาดา / IRCC", cluster: "embassy" },
  { slug: "notary-japan", q: "Notary สำหรับยื่นสถานทูตญี่ปุ่น", cluster: "embassy" },
  { slug: "notary-korea", q: "Notary สำหรับยื่นสถานทูตเกาหลีใต้", cluster: "embassy" },
  { slug: "notary-china", q: "Notary สำหรับยื่นสถานทูตจีน", cluster: "embassy" },
  { slug: "notary-mfa", q: "รับรองเอกสารที่กรมการกงสุล (MFA) ทำอย่างไร?", cluster: "apostille" },
  { slug: "notary-apostille-thailand", q: "ประเทศไทยออก Apostille ได้ไหม?", cluster: "apostille" },
  { slug: "notary-sukhumvit", q: "Notary Public แถวสุขุมวิท", cluster: "location" },
  { slug: "notary-silom", q: "Notary Public แถวสีลม", cluster: "location" },
  { slug: "notary-sathorn", q: "Notary Public แถวสาทร", cluster: "location" },
  { slug: "notary-asoke", q: "Notary Public แถวอโศก", cluster: "location" },
  { slug: "notary-phuket", q: "Notary Public ที่ภูเก็ต", cluster: "location" },
  { slug: "notary-pattaya", q: "Notary Public ที่พัทยา", cluster: "location" },
  { slug: "notary-chiangmai", q: "Notary Public ที่เชียงใหม่", cluster: "location" },
  { slug: "notary-english", q: "หา English-Speaking Notary ในกรุงเทพฯ", cluster: "location" },
  { slug: "notary-weekend", q: "Notary Public เปิดเสาร์-อาทิตย์ไหม?", cluster: "speed" },
  { slug: "notary-24hr", q: "Notary Public 24 ชั่วโมง / กรณีฉุกเฉิน", cluster: "speed" },
  { slug: "notary-rush", q: "Notary Public ด่วน เสร็จภายในกี่นาที?", cluster: "speed" },
  { slug: "notary-valid-months", q: "เอกสารที่ผ่าน Notary ใช้ได้กี่เดือน?", cluster: "process" },
  { slug: "notary-need-witness", q: "ต้องมีพยานตอนเซ็น Notary ไหม?", cluster: "process" },
  { slug: "notary-company-director", q: "กรรมการบริษัทมาเซ็น Notary ต้องเตรียมอะไร?", cluster: "process" },
  { slug: "notary-foreigner", q: "ชาวต่างชาติทำ Notary ในไทยได้ไหม?", cluster: "process" },
  { slug: "notary-multiple-copies", q: "รับรองสำเนา Notary หลายชุดคิดเงินอย่างไร?", cluster: "price" },
  { slug: "notary-package", q: "แพ็กเกจ Notary + MFA + สถานทูต ราคาเท่าไหร่?", cluster: "price" },
];

// Bulk expansion — synthesizes remaining ~440 curated Q slugs to reach 500+.
// Uses (topic × facet) matrices so every high-frequency 2026 Thai search intent
// resolves to a stable /notary/q/$slug URL with QAPage schema.
const DOC_QS: Array<[string, string, NotaryQA["cluster"]]> = [
  ["thabien-baan","ทะเบียนบ้านทำ Notary ต้องเตรียมอะไร?","documents"],
  ["id-card","บัตรประชาชนรับรอง Notary ได้ไหม?","documents"],
  ["work-permit","Work Permit ต้อง Notary เพื่อใช้ต่างประเทศไหม?","documents"],
  ["visa-copy","สำเนา Visa รับรอง Notary","documents"],
  ["yellow-book","Yellow Book (ทร.13) รับรอง Notary","documents"],
  ["pink-id","บัตรชมพู (คนต่างด้าว) รับรอง Notary","documents"],
  ["will","พินัยกรรมต้อง Notary ไหม?","legal"],
  ["title-deed","โฉนดที่ดิน (นส.4) รับรอง Notary","documents"],
  ["condo-title","หนังสือกรรมสิทธิ์ห้องชุด Notary","documents"],
  ["land-lease","สัญญาเช่าที่ดิน Notary","legal"],
  ["condo-lease","สัญญาเช่าคอนโด Notary ใช้ต่างประเทศ","legal"],
  ["employment-contract","สัญญาจ้างงาน Notary ต่างประเทศ","legal"],
  ["nda","NDA / สัญญารักษาความลับ Notary","legal"],
  ["mou","MOU / บันทึกความเข้าใจ Notary","legal"],
  ["shareholder-agreement","Shareholder Agreement Notary","legal"],
  ["board-resolution","Board Resolution / รายงานการประชุมกรรมการ Notary","legal"],
  ["audit-report","งบการเงินตรวจสอบ (Audit Report) Notary","documents"],
  ["tax-id","หนังสือรับรองภาษี (ภ.พ.20) Notary","documents"],
  ["financial-statement","งบการเงิน Notary เพื่อยื่นต่างประเทศ","documents"],
  ["invoice","Invoice / Commercial Invoice Notary","documents"],
  ["packing-list","Packing List Notary สำหรับส่งออก","documents"],
  ["bill-of-lading","Bill of Lading รับรอง Notary","documents"],
  ["fda-cert","หนังสือรับรอง อย. Notary","documents"],
  ["gmp-cert","GMP / HACCP Certificate Notary","documents"],
  ["iso-cert","ISO Certificate Notary","documents"],
  ["patent-doc","เอกสารสิทธิบัตร Notary","legal"],
  ["copyright-doc","เอกสารลิขสิทธิ์ Notary","legal"],
  ["adoption-doc","เอกสารรับบุตรบุญธรรม Notary","legal"],
  ["guardianship","หนังสือแต่งตั้งผู้ปกครอง Notary","legal"],
  ["consent-parent","หนังสือยินยอมของบิดามารดา Notary","legal"],
  ["translation-cert","หนังสือรับรองการแปล Notary","translation"],
  ["house-registration-en","แปล/รับรองทะเบียนบ้านเป็นภาษาอังกฤษ","translation"],
  ["marriage-cert-en","แปล/รับรองทะเบียนสมรสภาษาอังกฤษ","translation"],
  ["divorce-cert-en","แปล/รับรองใบหย่าภาษาอังกฤษ","translation"],
  ["degree-en","แปล/รับรองใบปริญญาภาษาอังกฤษ","translation"],
  ["transcript-en","แปล/รับรอง Transcript ภาษาอังกฤษ","translation"],
];

const EMBASSY_QS: Array<[string, string]> = [
  ["schengen","สถานทูต Schengen / ยุโรป"],["germany","สถานทูตเยอรมนี"],["france","สถานทูตฝรั่งเศส"],
  ["italy","สถานทูตอิตาลี"],["spain","สถานทูตสเปน"],["netherlands","สถานทูตเนเธอร์แลนด์"],
  ["sweden","สถานทูตสวีเดน"],["switzerland","สถานทูตสวิตเซอร์แลนด์"],["denmark","สถานทูตเดนมาร์ก"],
  ["norway","สถานทูตนอร์เวย์"],["newzealand","สถานทูตนิวซีแลนด์"],["india","สถานทูตอินเดีย"],
  ["taiwan","สำนักงานเศรษฐกิจไทเป"],["hongkong","หน่วยงาน Hong Kong"],["singapore","สถานทูตสิงคโปร์"],
  ["malaysia","สถานทูตมาเลเซีย"],["vietnam","สถานทูตเวียดนาม"],["indonesia","สถานทูตอินโดนีเซีย"],
  ["philippines","สถานทูตฟิลิปปินส์"],["uae","สถานทูตสหรัฐอาหรับเอมิเรตส์"],["saudi","สถานทูตซาอุดีอาระเบีย"],
  ["qatar","สถานทูตกาตาร์"],["russia","สถานทูตรัสเซีย"],["southafrica","สถานทูตแอฟริกาใต้"],
];

const CITY_QS: Array<[string, string]> = [
  ["thonglor","ทองหล่อ"],["ekkamai","เอกมัย"],["ari","อารีย์"],["phromphong","พร้อมพงษ์"],
  ["nana","นานา"],["phloen-chit","เพลินจิต"],["chidlom","ชิดลม"],["ratchada","รัชดาภิเษก"],
  ["ladprao","ลาดพร้าว"],["rama9","พระราม 9"],["huai-khwang","ห้วยขวาง"],["bang-na","บางนา"],
  ["srinakarin","ศรีนครินทร์"],["ramkhamhaeng","รามคำแหง"],["bangkapi","บางกะปิ"],
  ["nonthaburi","นนทบุรี"],["pak-kret","ปากเกร็ด"],["samut-prakan","สมุทรปราการ"],
  ["nakhon-pathom","นครปฐม"],["chonburi","ชลบุรี"],["rayong","ระยอง"],["hua-hin","หัวหิน"],
  ["cha-am","ชะอำ"],["krabi","กระบี่"],["koh-samui","เกาะสมุย"],["chiang-rai","เชียงราย"],
  ["khon-kaen","ขอนแก่น"],["udon-thani","อุดรธานี"],["nakhon-ratchasima","นครราชสีมา"],
  ["hat-yai","หาดใหญ่"],["songkhla","สงขลา"],["ayutthaya","อยุธยา"],["nakhon-si","นครศรีธรรมราช"],
];

const INTENT_QS: Array<[string, string]> = [
  ["quote","Notary ขอใบเสนอราคา"],["urgent","Notary งานเร่งด่วน"],
  ["reliable","วิธีตรวจสอบ Notary ที่น่าเชื่อถือ"],["experienced","Notary ที่มีประสบการณ์"],
  ["boi","Notary สำหรับ BOI"],["dbd","Notary สำหรับ DBD"],
  ["sec","Notary สำหรับ ก.ล.ต. (SEC)"],["bot","Notary สำหรับธนาคารแห่งประเทศไทย"],
  ["holiday","Notary วันหยุดนักขัตฤกษ์"],["evening","Notary เย็น / หลังเลิกงาน"],
  ["hotel","Notary ที่โรงแรม"],["hospital","Notary ที่โรงพยาบาล"],
  ["prison","Notary ในเรือนจำ"],["airport","Notary ที่สนามบิน"],
];

const COMPARE_QS: Array<[string, string]> = [
  ["notary-vs-lawyer","Notary ต่างกับทนายทั่วไปอย่างไร?"],
  ["notary-vs-embassy","Notary ต่างกับสถานทูตอย่างไร?"],
  ["notary-vs-mfa","Notary ต่างกับ MFA อย่างไร?"],
  ["notary-vs-attorney","Notarial Attorney vs Notary Public ต่างกันอย่างไร?"],
  ["notary-vs-consul","Notary ต่างกับกงสุลอย่างไร?"],
  ["thai-vs-us-notary","Notary ไทย vs Notary สหรัฐ ต่างกันอย่างไร?"],
  ["thai-vs-uk-notary","Notary ไทย vs UK Solicitor Notary"],
  ["signed-vs-certified","รับรองลายเซ็น vs รับรองสำเนาต่างกันอย่างไร?"],
  ["copy-vs-original","Notary ต้องใช้ต้นฉบับหรือสำเนา?"],
];

const PRICE_QS: Array<[string, string]> = [
  ["price-poa","ราคา Notary หนังสือมอบอำนาจ"],
  ["price-affidavit","ราคา Notary Affidavit"],
  ["price-passport-copy","ราคารับรองสำเนาพาสปอร์ต"],
  ["price-degree","ราคารับรองใบปริญญา"],
  ["price-transcript","ราคารับรอง Transcript"],
  ["price-mfa","ค่าธรรมเนียม MFA / กรมการกงสุล"],
  ["price-embassy","ค่าธรรมเนียมสถานทูตแต่ละประเทศ"],
  ["price-mobile","ค่าบริการ Mobile Notary นอกสถานที่"],
  ["price-translation","ราคาแปลรับรองต่อหน้า"],
  ["price-package","แพ็กเกจครบวงจร Notary+MFA+Embassy"],
  ["price-bulk","ราคาส่วนลดสำหรับเอกสารจำนวนมาก"],
  ["price-corporate","ราคา Notary สำหรับลูกค้าองค์กร"],
];

const PROCESS_QS: Array<[string, string]> = [
  ["book-appointment","จองคิว Notary อย่างไร?"],
  ["walk-in","Walk-in ทำ Notary ได้ไหม?"],
  ["pay-method","จ่ายเงิน Notary ด้วยวิธีไหนได้บ้าง?"],
  ["receipt","ขอใบเสร็จ/ใบกำกับภาษี Notary"],
  ["signature-format","รูปแบบลายเซ็นที่ถูกต้องบน Notary"],
  ["stamp-seal","ตราประทับ Notary หน้าตาเป็นอย่างไร?"],
  ["notary-ribbon","ริบบิ้น 3 สีบนเอกสาร Notary หมายถึงอะไร?"],
  ["multi-page","เอกสารหลายหน้า Notary รับรองอย่างไร?"],
  ["binding","การเข้าเล่มเอกสาร Notary"],
  ["reject-reason","เอกสารถูกปฏิเสธ Notary เพราะอะไรได้บ้าง?"],
  ["mistake-fix","เซ็นผิดในเอกสาร Notary แก้ยังไง?"],
  ["reissue","ขอออก Notary ฉบับใหม่แทนของเดิม"],
  ["copy-of-notary","ขอสำเนา Notary ย้อนหลัง"],
];

const APOSTILLE_QS: Array<[string, string]> = [
  ["hague-list","ประเทศภาคี Hague Apostille มีอะไรบ้าง?"],
  ["hague-thailand-status","ไทยจะเข้าร่วม Hague Apostille เมื่อไหร่?"],
  ["apostille-us","Apostille จากสหรัฐใช้ในไทยได้ไหม?"],
  ["apostille-uk","Apostille จาก UK ใช้ในไทย"],
  ["apostille-china","Apostille จีนใช้ในไทย"],
  ["apostille-india","Apostille อินเดียใช้ในไทย"],
  ["apostille-korea","Apostille เกาหลีใต้ใช้ในไทย"],
  ["apostille-japan","Apostille ญี่ปุ่นใช้ในไทย"],
  ["apostille-eu","Apostille ประเทศ EU ใช้ในไทย"],
  ["legalization-flow","ขั้นตอน Legalization เต็มรูปแบบ"],
  ["mfa-appointment","จองคิว MFA ล่วงหน้าอย่างไร?"],
  ["mfa-express","MFA แบบด่วน (Express) มีไหม?"],
];

const TRANSLATION_QS: Array<[string, string]> = [
  ["translation-english","แปลรับรองภาษาอังกฤษ"],
  ["translation-chinese","แปลรับรองภาษาจีน"],
  ["translation-japanese","แปลรับรองภาษาญี่ปุ่น"],
  ["translation-korean","แปลรับรองภาษาเกาหลี"],
  ["translation-arabic","แปลรับรองภาษาอาหรับ"],
  ["translation-french","แปลรับรองภาษาฝรั่งเศส"],
  ["translation-german","แปลรับรองภาษาเยอรมัน"],
  ["translation-russian","แปลรับรองภาษารัสเซีย"],
  ["translation-vietnamese","แปลรับรองภาษาเวียดนาม"],
  ["translation-indonesian","แปลรับรองภาษาอินโดนีเซีย"],
  ["sworn-vs-certified","Sworn Translation vs Certified Translation ต่างกันอย่างไร?"],
  ["naati-thailand","NAATI แปลรับรองในไทยได้ไหม?"],
];

const ONLINE_QS: Array<[string, string]> = [
  ["docusign-notary","DocuSign ใช้แทน Notary ได้ไหม?"],
  ["digital-signature","ลายเซ็นดิจิทัลเทียบเท่า Notary ไหม?"],
  ["e-stamp","e-Stamp / อากรแสตมป์อิเล็กทรอนิกส์"],
  ["video-conference-notary","Notary ผ่าน Zoom / Video Call"],
  ["blockchain-notary","Blockchain Notary / NFT Notary"],
  ["ai-notary","AI Notary ในไทยมีไหม?"],
];

function pushQ(list: Array<[string, string]>, prefix: string, cluster: NotaryQA["cluster"]) {
  for (const [slug, label] of list) {
    STUB_QUESTIONS.push({ slug: `${prefix}-${slug}`, q: `${label} — ${prefix === "city" ? "Notary Public" : ""}`.trim(), cluster });
  }
}

for (const [slug, q, cluster] of DOC_QS) STUB_QUESTIONS.push({ slug: `doc-${slug}`, q, cluster });
for (const [slug, label] of EMBASSY_QS) STUB_QUESTIONS.push({ slug: `emb-${slug}`, q: `Notary สำหรับ${label}ต้องทำอย่างไร?`, cluster: "embassy" });
for (const [slug, label] of CITY_QS) STUB_QUESTIONS.push({ slug: `city-${slug}`, q: `Notary Public ที่${label} — เปิดที่ไหน ราคาเท่าไหร่?`, cluster: "location" });
for (const [slug, label] of INTENT_QS) STUB_QUESTIONS.push({ slug: `intent-${slug}`, q: label, cluster: "process" });
for (const [slug, label] of COMPARE_QS) STUB_QUESTIONS.push({ slug: `cmp-${slug}`, q: label, cluster: "compare" });
for (const [slug, label] of PRICE_QS) STUB_QUESTIONS.push({ slug: `p-${slug}`, q: label, cluster: "price" });
for (const [slug, label] of PROCESS_QS) STUB_QUESTIONS.push({ slug: `pr-${slug}`, q: label, cluster: "process" });
for (const [slug, label] of APOSTILLE_QS) STUB_QUESTIONS.push({ slug: `apo-${slug}`, q: label, cluster: "apostille" });
for (const [slug, label] of TRANSLATION_QS) STUB_QUESTIONS.push({ slug: `tr-${slug}`, q: label, cluster: "translation" });
for (const [slug, label] of ONLINE_QS) STUB_QUESTIONS.push({ slug: `on-${slug}`, q: label, cluster: "online" });

// Matrix expansions to guarantee 500+ coverage: (document × embassy) high-value pairs
const DOC_KEYS = ["poa","affidavit","degree","transcript","marriage","divorce","birth","single","passport","bank"];
const EMB_KEYS = ["us","uk","au","ca","de","fr","jp","kr","cn"];
for (const d of DOC_KEYS) for (const e of EMB_KEYS) {
  STUB_QUESTIONS.push({
    slug: `matrix-${d}-${e}`,
    q: `Notary ${d.toUpperCase()} สำหรับสถานทูต ${e.toUpperCase()} ต้องทำอย่างไร?`,
    cluster: "embassy",
  });
}
void pushQ; // reserved helper

// (document × city) matrix — top 12 docs × top 16 cities = 192 pairs
const DOC_KEYS2 = ["poa","affidavit","degree","transcript","marriage","divorce","birth","single","passport","houseregis","bankstmt","policecert"];
const DOC_LABELS: Record<string,string> = {
  poa:"หนังสือมอบอำนาจ",affidavit:"Affidavit",degree:"ใบปริญญา",transcript:"Transcript",
  marriage:"ทะเบียนสมรส",divorce:"ใบหย่า",birth:"สูติบัตร",single:"หนังสือรับรองโสด",
  passport:"พาสปอร์ต",houseregis:"ทะเบียนบ้าน",bankstmt:"Bank Statement",policecert:"Police Clearance"
};
const CITY_KEYS2 = ["sukhumvit","silom","sathorn","asoke","thonglor","phromphong","ratchada","ladprao","bangna","phuket","pattaya","chiangmai","huahin","kohsamui","hatyai","khonkaen"];
const CITY_LABELS: Record<string,string> = {
  sukhumvit:"สุขุมวิท",silom:"สีลม",sathorn:"สาทร",asoke:"อโศก",thonglor:"ทองหล่อ",phromphong:"พร้อมพงษ์",
  ratchada:"รัชดา",ladprao:"ลาดพร้าว",bangna:"บางนา",phuket:"ภูเก็ต",pattaya:"พัทยา",
  chiangmai:"เชียงใหม่",huahin:"หัวหิน",kohsamui:"เกาะสมุย",hatyai:"หาดใหญ่",khonkaen:"ขอนแก่น"
};
for (const d of DOC_KEYS2) for (const c of CITY_KEYS2) {
  STUB_QUESTIONS.push({
    slug: `dc-${d}-${c}`,
    q: `Notary ${DOC_LABELS[d]} ที่${CITY_LABELS[c]} ราคาเท่าไหร่ ทำที่ไหน?`,
    cluster: "location",
  });
}

// Rare-tail long-tail queries
const TAIL_QS: Array<[string, string, NotaryQA["cluster"]]> = [
  ["notary-english-attorney","หา English-Speaking Attorney Notary ในกรุงเทพ","location"],
  ["notary-chinese-attorney","Notary ภาษาจีน / 中文公证","location"],
  ["notary-japanese-attorney","Notary ภาษาญี่ปุ่น / 日本語対応","location"],
  ["notary-korean-attorney","Notary ภาษาเกาหลี / 한국어","location"],
  ["notary-female","หา Notary ผู้หญิง","location"],
  ["notary-lgbtq","Notary สำหรับคู่รัก LGBTQ+","legal"],
  ["notary-elderly","บริการ Notary สำหรับผู้สูงอายุ / ผู้ป่วยติดเตียง","process"],
  ["notary-disability","Notary สำหรับผู้พิการ","process"],
  ["notary-signature-only","รับรองเฉพาะลายเซ็นอย่างเดียว","process"],
  ["notary-copy-only","รับรองเฉพาะสำเนาอย่างเดียว","process"],
  ["notary-original-lost","ถ้าต้นฉบับหาย ทำ Notary ได้ไหม?","process"],
  ["notary-old-document","เอกสารเก่า 10-20 ปี ทำ Notary ได้ไหม?","documents"],
  ["notary-damaged-document","เอกสารเสียหาย/ฉีกขาด รับรอง Notary","documents"],
  ["notary-water-damage","เอกสารเปียกน้ำ ทำ Notary ได้ไหม?","documents"],
  ["notary-thai-driving","รับรองใบขับขี่ไทยสำหรับใช้ต่างประเทศ","documents"],
  ["notary-motorcycle-license","รับรองใบขับขี่มอเตอร์ไซค์สากล","documents"],
  ["notary-boat-license","รับรองใบอนุญาตขับเรือ","documents"],
  ["notary-pilot-license","รับรองใบอนุญาตนักบิน","documents"],
  ["notary-military","รับรองเอกสารทหาร (สด.8, สด.9)","documents"],
  ["notary-vaccine","รับรอง Vaccine Certificate นานาชาติ","documents"],
  ["notary-covid","รับรองผลตรวจ COVID-19 / RT-PCR","documents"],
  ["notary-insurance","รับรองกรมธรรม์ประกันสุขภาพ","documents"],
  ["notary-life-insurance","รับรองกรมธรรม์ประกันชีวิต","documents"],
  ["notary-death-cert","รับรองใบมรณบัตร","documents"],
  ["notary-cremation","รับรองใบฌาปนกิจ","documents"],
  ["notary-name-change","รับรองใบเปลี่ยนชื่อ-สกุล","documents"],
  ["notary-adoption","รับรองเอกสารรับบุตรบุญธรรม","legal"],
  ["notary-inheritance","รับรองพินัยกรรม / เอกสารมรดก","legal"],
  ["notary-real-estate","รับรองเอกสารซื้อขายอสังหาริมทรัพย์","legal"],
  ["notary-condo-purchase","รับรองสัญญาซื้อขายคอนโด","legal"],
  ["notary-house-purchase","รับรองสัญญาซื้อขายบ้าน","legal"],
  ["notary-car-purchase","รับรองสัญญาซื้อขายรถยนต์","legal"],
  ["notary-business-sale","รับรองสัญญาซื้อขายกิจการ (M&A)","legal"],
  ["notary-franchise","รับรองสัญญาแฟรนไชส์","legal"],
  ["notary-license-agreement","รับรอง License Agreement","legal"],
  ["notary-loan-agreement","รับรองสัญญากู้ยืมเงิน","legal"],
  ["notary-guarantee","รับรองสัญญาค้ำประกัน","legal"],
  ["notary-mortgage","รับรองสัญญาจำนอง","legal"],
  ["notary-pledge","รับรองสัญญาจำนำ","legal"],
  ["notary-partnership","รับรองสัญญาห้างหุ้นส่วน","legal"],
  ["notary-joint-venture","รับรอง Joint Venture Agreement","legal"],
  ["notary-service-agreement","รับรอง Service Agreement","legal"],
  ["notary-consulting","รับรอง Consulting Agreement","legal"],
  ["notary-agency","รับรอง Agency Agreement","legal"],
  ["notary-supply","รับรอง Supply Agreement","legal"],
  ["notary-purchase-order","รับรอง Purchase Order (PO)","legal"],
  ["notary-letter-of-intent","รับรอง Letter of Intent (LOI)","legal"],
  ["notary-termination","รับรองหนังสือบอกเลิกสัญญา","legal"],
  ["notary-settlement","รับรองสัญญาประนีประนอม","legal"],
  ["notary-court-order","รับรองคำสั่งศาล","legal"],
  ["notary-judgment","รับรองคำพิพากษา","legal"],
  ["notary-arbitration","รับรองคำชี้ขาดอนุญาโตตุลาการ","legal"],
  ["notary-visa-usa-b1","Notary สำหรับวีซ่าธุรกิจสหรัฐ (B1/B2)","embassy"],
  ["notary-visa-uk-family","Notary สำหรับวีซ่าครอบครัว UK","embassy"],
  ["notary-visa-au-partner","Notary สำหรับ Australia Partner Visa (820/801)","embassy"],
  ["notary-visa-ca-spouse","Notary สำหรับ Canada Spousal Sponsorship","embassy"],
  ["notary-student-visa","Notary สำหรับวีซ่านักเรียน","embassy"],
  ["notary-work-visa","Notary สำหรับวีซ่าทำงานต่างประเทศ","embassy"],
  ["notary-tourist-visa","Notary สำหรับวีซ่าท่องเที่ยว","embassy"],
  ["notary-retirement-visa","Notary สำหรับ Retirement Visa","embassy"],
  ["notary-golden-visa","Notary สำหรับ Golden Visa (โปรตุเกส/UAE)","embassy"],
  ["notary-investor-visa","Notary สำหรับ Investor Visa","embassy"],
  ["notary-us-embassy-bkk","จองคิว US Embassy Bangkok หลัง Notary","embassy"],
  ["notary-uk-embassy-bkk","จองคิว UK Embassy Bangkok หลัง Notary","embassy"],
  ["notary-au-embassy-bkk","จองคิว Australia Embassy Bangkok หลัง Notary","embassy"],
];
for (const [slug, q, cluster] of TAIL_QS) STUB_QUESTIONS.push({ slug: `tail-${slug}`, q, cluster });

for (const s of STUB_QUESTIONS) {
  NOTARY_QA.push({
    slug: s.slug,
    q: s.q,
    aShort: `${s.q} — โดยทนายผู้ได้รับใบอนุญาตของ Thai Notary Law ${P} ระยะเวลาแจ้งหลังตรวจเอกสาร รับเอกสารต่อเนื่องไปยัง MFA และสถานทูตปลายทางได้ในขั้นตอนเดียว โทร 094-895-8999 หรือ LINE @Thainotary`,
    aLong: `เราให้บริการ ${s.q.replace(/[?]/g, "").trim()} โดยทนายที่ขึ้นทะเบียนกับสภาทนายความแห่งประเทศไทย รับรองลายมือชื่อและสำเนาต่อหน้า พร้อมนำส่งกรมการกงสุลและสถานทูตปลายทางในขั้นตอนเดียว เอกสารได้รับการยอมรับในกว่า 60 ประเทศทั่วโลก`,
    synonyms: s.synonyms ?? [],
    cluster: s.cluster,
  });
}

import { NOTARY_QA_WAVE2 } from "./questions-wave2";
for (const qa of NOTARY_QA_WAVE2) {
  if (!NOTARY_QA.some((x) => x.slug === qa.slug)) NOTARY_QA.push(qa);
}


import { NOTARY_QA_WAVE3 } from "./questions-wave3";
for (const qa of NOTARY_QA_WAVE3) {
  if (!NOTARY_QA.some((x) => x.slug === qa.slug)) NOTARY_QA.push(qa);
}

export const NOTARY_QA_BY_SLUG: Record<string, NotaryQA> = Object.fromEntries(NOTARY_QA.map((q) => [q.slug, q]));
